import { useState, useEffect } from "react";
import { HelpCircle, X, ChevronRight, ChevronLeft, Banknote, Baby, User as UserIcon, BadgeCheck, MousePointerClick, Receipt, Trash2 } from "lucide-react";

/**
 * Help / Onboarding for the Kassa page.
 * - Shows a 5-step tour on first visit (or via "?" button).
 * - Persists "seen" flag in localStorage.
 */
const TOUR_KEY = "kassa_tour_seen_v1";

const STEPS = [
  {
    title: "Willkommen an der Kassa!",
    body: (
      <>
        <p className="mb-3">Diese kurze Tour zeigt dir die 4 wichtigsten Schritte.</p>
        <p className="text-zinc-500 text-sm">Du kannst die Hilfe jederzeit erneut mit dem <strong className="text-zinc-900">?</strong>-Knopf oben rechts öffnen.</p>
      </>
    ),
  },
  {
    title: "1. Reservierung auswählen (oder Abendkasse)",
    body: (
      <>
        <p className="mb-3">Rechts siehst du alle Reservierungen. <strong>Tippe auf den Namen</strong>, dann werden die reservierten Sitze im Saalplan <span className="inline-block w-4 h-4 bg-blue-400 rounded align-middle" /> <strong>blau</strong>.</p>
        <p>Kommt jemand ohne Reservierung? Tippe auf <strong>„Ohne Reservierung"</strong> – dann sind nur die <span className="inline-block w-4 h-4 bg-zinc-200 rounded align-middle" /> <strong>grauen (freien)</strong> Sitze antippbar.</p>
      </>
    ),
  },
  {
    title: "2. Karten-Art wählen, dann Sitz antippen",
    body: (
      <>
        <p className="mb-3">Wähle in der Mitte oben, welche Karte gerade an der Reihe ist:</p>
        <div className="flex gap-3 mb-3 text-base">
          <span className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded"><Baby className="h-4 w-4" /> Kind</span>
          <span className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded"><UserIcon className="h-4 w-4" /> Erwachsen</span>
          <span className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded"><BadgeCheck className="h-4 w-4" /> SAV</span>
        </div>
        <p>Tippe dann den passenden Sitz im Saalplan – die Karte wandert in den Cart in der Mitte.</p>
      </>
    ),
  },
  {
    title: "3. Gutschein? Falsch geklickt?",
    body: (
      <>
        <p className="mb-3"><strong>Gutschein eingelöst:</strong> Der lila <span className="text-purple-700 font-bold">+</span>-Knopf bei „Gutschein eingelöst" zieht den teuersten Karten-Preis von der Summe ab.</p>
        <p className="mb-3"><strong>Falsch geklickt:</strong> Direkt am Sitz im Cart steht ein <strong>X</strong> – tippen entfernt den Sitz wieder.</p>
        <p>Oder oben rechts der <strong>Rückgängig</strong>-Knopf macht den letzten Klick zurück.</p>
      </>
    ),
  },
  {
    title: "4. Kassieren – Bar oder Rechnung",
    body: (
      <>
        <p className="mb-3 flex items-center gap-2"><Banknote className="h-5 w-5 text-emerald-700" /> <strong>Bar</strong>: Numpad rechts → Betrag eingeben → grüner Bar-Knopf. Es kommt eine Rückfrage mit Übersicht, bevor wirklich gespeichert wird.</p>
        <p className="flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-700" /> <strong>Rechnung</strong>: blauer Rechnung-Knopf → Verein/Adresse/E-Mail ausfüllen → fertig. Die Rechnung kann gleich per E-Mail verschickt werden.</p>
      </>
    ),
  },
];

export function HelpButton({ onClick }) {
  return (
    <button
      data-testid="kassa-help-btn"
      onClick={onClick}
      title="Hilfe anzeigen"
      className="h-12 w-12 rounded-full bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-900 flex items-center justify-center text-xl font-black"
    >
      <HelpCircle className="h-6 w-6" />
    </button>
  );
}

export function HelpDialog({ open, onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open) return null;
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;
  const current = STEPS[step];

  const finish = () => {
    try { localStorage.setItem(TOUR_KEY, "1"); } catch {}
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={finish}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Hilfe · Schritt {step + 1} von {STEPS.length}</p>
          <button onClick={finish} className="h-10 w-10 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid="help-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-8 py-8">
          <h2 className="text-3xl font-black tracking-tight mb-5">{current.title}</h2>
          <div className="text-lg text-zinc-700 leading-relaxed">{current.body}</div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between gap-2">
          <button data-testid="help-prev" disabled={isFirst} onClick={() => setStep(s => Math.max(0, s - 1))}
            className="h-14 px-5 rounded-lg border border-zinc-200 hover:bg-zinc-50 font-bold text-base flex items-center gap-2 disabled:opacity-30">
            <ChevronLeft className="h-5 w-5" /> Zurück
          </button>
          <div className="flex gap-2">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-2 w-2 rounded-full ${i === step ? "bg-zinc-950" : "bg-zinc-300"}`} />
            ))}
          </div>
          {isLast ? (
            <button data-testid="help-finish" onClick={finish}
              className="h-14 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base">
              Verstanden!
            </button>
          ) : (
            <button data-testid="help-next" onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              className="h-14 px-6 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white font-bold text-base flex items-center gap-2">
              Weiter <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function useFirstVisitTour() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let seen = "0";
    try { seen = localStorage.getItem(TOUR_KEY) || "0"; } catch {}
    if (seen !== "1") setOpen(true);
  }, []);
  return [open, setOpen];
}

// Keep a few helper icons that the parent can reuse for category buttons
export { MousePointerClick, Trash2, Banknote, Receipt, Baby, BadgeCheck };
export { UserIcon as User };
